/**
 * LoginPage — Zitadel SSO (primary) + legacy email/password fallback.
 *
 * The "Sign in with AxonBOS Identity" button redirects to Zitadel's hosted UI
 * at https://dev-auth.axonbos.com. Email verification, password reset, MFA,
 * and registration are all handled by Zitadel — no UI to build here. The
 * email/password form below is kept only as a fallback during the migration
 * window and will be removed once SSO is verified across the fleet.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { useLogin } from '@refinedev/core';
import { Button, Input, Label, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@yesbos/ui';
import { Loader2, Lock, LogIn } from 'lucide-react';
import { startZitadelLogin } from '@/lib/zitadel-oidc';

export function LoginPage() {
  const navigate = useNavigate();
  const { mutate: login, isLoading } = useLogin();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    login(
      { email, password },
      {
        onSuccess: () => navigate('/'),
        onError: (err: any) => {
          setError(err?.message ?? 'Login failed. Please check your credentials.');
        },
      },
    );
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-muted)] p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-primary)]">
            <Lock className="h-6 w-6 text-[var(--color-primary-foreground)]" />
          </div>
          <CardTitle className="text-2xl">
            <span className="text-[var(--color-primary)]">HR</span>OS
          </CardTitle>
          <CardDescription>
            Sign in to your HR Operating System
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-3 pb-0">
          <Button
            type="button"
            className="w-full"
            onClick={() => startZitadelLogin('login')}
          >
            <LogIn className="mr-2 h-4 w-4" />
            Sign in with AxonBOS Identity
          </Button>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => startZitadelLogin('create')}
          >
            Create AxonBOS account
          </Button>
          <p className="text-xs text-center text-[var(--color-muted-foreground)] pt-1">
            One sign-in works across Laabha, YesBOS and HROS. Email
            verification, password reset and MFA all happen here.
          </p>
          <div className="relative my-3">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[var(--color-border)]" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-[var(--color-card)] px-2 text-[10px] uppercase tracking-wider text-[var(--color-muted-foreground)]">
                or legacy login
              </span>
            </div>
          </div>
        </CardContent>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <div className="rounded-md bg-[var(--color-destructive)]/10 p-3 text-sm text-[var(--color-destructive)]">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
          </CardContent>

          <CardFooter>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign In
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
